/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit, signal} from '@angular/core';
import {EsciComponent} from '../esci.component';
import {
    IcdSubscriptionListItem,
    IcdSubscriptionModelYearOption,
    IcdSubscriptionProgramOption
} from '../../model/icdSubscription.model';
import {environment} from '../../../environments/environment';

@Component({
    selector: 'app-icd-subscriptions',
    templateUrl: './icd-subscriptions.component.html',
    styleUrls: ['./icd-subscriptions.component.scss'],
    standalone: false
})
export class IcdSubscriptionsComponent extends EsciComponent implements OnInit {
    permissionUrl = '/esciUI/icdSubscriptionPermission';

    private readonly loadingSignal = signal(true);
    private readonly subscriptionsSignal = signal<IcdSubscriptionListItem[]>([]);
    private readonly programOptionsSignal = signal<IcdSubscriptionProgramOption[]>([]);
    private readonly modelYearOptionsSignal = signal<IcdSubscriptionModelYearOption[]>([]);
    private readonly programQuerySignal = signal('');
    /** Approximate ICD count for the group currently selected in the "Add Program + Model Year" controls, or null before a preview has been fetched. */
    private readonly groupPreviewCountSignal = signal<number | null>(null);

    newIcdId: number | null = null;
    selectedProgramLabel = '';
    selectedProgramId: number | null = null;
    selectedModelYearId: number | null = null;
    saving = false;

    ngOnInit(): void {
        this.permission.set(false);
        this.esciService.checkPermission(this);
        this.loadSubscriptions();
        this.loadProgramOptions();
        this.loadModelYearOptions();
    }

    get loading(): boolean {
        return this.loadingSignal();
    }

    set loading(value: boolean) {
        this.loadingSignal.set(value);
    }

    get subscriptions(): IcdSubscriptionListItem[] {
        return this.subscriptionsSignal();
    }

    set subscriptions(value: IcdSubscriptionListItem[]) {
        this.subscriptionsSignal.set(value || []);
    }

    get programOptions(): IcdSubscriptionProgramOption[] {
        return this.programOptionsSignal();
    }

    get modelYearOptions(): IcdSubscriptionModelYearOption[] {
        return this.modelYearOptionsSignal();
    }

    get groupPreviewCount(): number | null {
        return this.groupPreviewCountSignal();
    }

    /**
     * Absolute link into the legacy ICD Search grid, pre-filtered to the current user's effective subscriptions.
     * Mirrors the existing "ICD Issue" cross-app link technique (`environment.apiUrl` + `/ui/...` path, opened via
     * `target="_blank"`) - see `io-matrix-workflow.component.ts`'s `resolveAbsoluteUiPath`. The `icdSubscriptionSearch`
     * flag is a symbolic marker resolved server-side against the authenticated session; no ICD id list travels here.
     */
    get subscriptionSearchGridUrl(): string {
        const baseUrl = String(environment.apiUrl || window.location.origin).replace(/\/+$/, '');
        return `${baseUrl}/ui/icds?pageNo=1&icdSubscriptionSearch=true`;
    }

    get filteredProgramOptions(): string[] {
        const query = (this.programQuerySignal() || '').trim().toLowerCase();
        const labels = this.programOptionsSignal().map(option => option.programName);
        if (!query) {
            return labels;
        }
        return labels.filter(label => (label || '').toLowerCase().includes(query));
    }

    get modelYearSelectOptions(): { label: string; value: number }[] {
        return this.modelYearOptionsSignal().map(option => ({label: option.year, value: option.id}));
    }

    get canPreviewGroup(): boolean {
        return !!this.selectedProgramId && !!this.selectedModelYearId;
    }

    filterProgramSuggestions(event: { query?: string }): void {
        this.programQuerySignal.set(event?.query ?? '');
    }

    onProgramSelected(): void {
        this.programQuerySignal.set(this.selectedProgramLabel || '');
        this.selectedProgramId = this.programOptionsSignal()
            .find(option => option.programName === this.selectedProgramLabel)?.id ?? null;
        this.groupPreviewCountSignal.set(null);
    }

    onProgramCleared(): void {
        this.selectedProgramLabel = '';
        this.selectedProgramId = null;
        this.programQuerySignal.set('');
        this.groupPreviewCountSignal.set(null);
    }

    onModelYearSelected(): void {
        this.groupPreviewCountSignal.set(null);
    }

    previewGroup(): void {
        if (!this.canPreviewGroup) {
            return;
        }
        this.esciService.getIcdSubscriptionGroupPreviewCount(this.selectedProgramId, this.selectedModelYearId).subscribe({
            next: count => this.groupPreviewCountSignal.set(count ?? 0),
            error: () => {
                this.groupPreviewCountSignal.set(null);
                this.messageService.processError('Failed to preview the number of ICDs for that Program + Model Year.');
            }
        });
    }

    addIndividualSubscription(): void {
        if (!this.newIcdId) {
            this.messageService.processError('Enter an ICD ID to subscribe to.');
            return;
        }
        this.saving = true;
        this.esciService.addIcdSubscription(this.newIcdId).subscribe({
            next: () => {
                this.saving = false;
                this.newIcdId = null;
                this.loadSubscriptions();
                this.messageService.processSuccess('Subscribed to ICD.');
            },
            error: (err) => {
                this.saving = false;
                this.messageService.processError(err?.error?.message || 'Failed to subscribe to that ICD.');
            }
        });
    }

    removeIndividualSubscription(icdId: number): void {
        this.loading = true;
        this.esciService.removeIcdSubscription(icdId).subscribe({
            next: () => this.loadSubscriptions(),
            error: (err) => {
                this.loading = false;
                this.messageService.processError(err?.error?.message || 'Failed to remove that ICD subscription.');
            }
        });
    }

    confirmAddGroupSubscription(): void {
        if (!this.canPreviewGroup) {
            this.messageService.processError('Select a Program and Model Year first.');
            return;
        }
        this.saving = true;
        this.esciService.addIcdSubscriptionGroup(this.selectedProgramId, this.selectedModelYearId).subscribe({
            next: () => {
                this.saving = false;
                this.onProgramCleared();
                this.selectedModelYearId = null;
                this.loadSubscriptions();
                this.messageService.processSuccess('Subscribed to Program + Model Year group.');
            },
            error: (err) => {
                this.saving = false;
                this.messageService.processError(err?.error?.message
                    || 'Failed to add that Program + Model Year subscription.');
            }
        });
    }

    removeGroupSubscription(row: IcdSubscriptionListItem): void {
        const groupIds = this.parseGroupSource(row.source);
        if (!groupIds) {
            return;
        }
        this.loading = true;
        this.esciService.removeIcdSubscriptionGroup(groupIds.programId, groupIds.modelYearId).subscribe({
            next: () => this.loadSubscriptions(),
            error: (err) => {
                this.loading = false;
                this.messageService.processError(err?.error?.message || 'Failed to remove that group subscription.');
            }
        });
    }

    isGroupSource(source: string): boolean {
        return !!source && source.startsWith('GROUP:');
    }

    sourceLabel(row: IcdSubscriptionListItem): string {
        if (!this.isGroupSource(row.source)) {
            return 'Individual';
        }
        return `Group (${row.programName || ''} / ${row.modelYear || ''})`;
    }

    private loadSubscriptions(): void {
        this.loading = true;
        this.esciService.getIcdSubscriptions().subscribe({
            next: (subscriptions) => {
                this.subscriptions = subscriptions || [];
                this.loading = false;
            },
            error: () => {
                this.loading = false;
                this.subscriptions = [];
                this.messageService.processError('Failed to load your ICD subscriptions.');
            }
        });
    }

    private loadProgramOptions(): void {
        this.esciService.getIcdSubscriptionProgramOptions().subscribe({
            next: (options) => this.programOptionsSignal.set(options || []),
            error: () => this.programOptionsSignal.set([])
        });
    }

    private loadModelYearOptions(): void {
        this.esciService.getIcdSubscriptionModelYearOptions().subscribe({
            next: (options) => this.modelYearOptionsSignal.set(options || []),
            error: () => this.modelYearOptionsSignal.set([])
        });
    }

    /** Parses the "GROUP:{programId}/{modelYearId}" source tag written by ICDSubscriptionServiceImpl. */
    private parseGroupSource(source: string): { programId: number; modelYearId: number } | null {
        const match = /^GROUP:(-?\d+)\/(-?\d+)$/.exec(source || '');
        if (!match) {
            return null;
        }
        return {programId: Number(match[1]), modelYearId: Number(match[2])};
    }
}
